import React, { useState, useEffect } from "react";
import { Card, Row, InputGroup } from "react-bootstrap";
import { CForm, CCol, CFormLabel, CFormInput, CButton } from "@coreui/react";
import { useDispatch } from "react-redux";
import { checkEmpty, toastMessage } from "../../../helpers/utility.js";
import {
  fetchSupplierData,
  createSupplierInvoice,
} from "../../../redux/slices/SupplierInvoiceSlice.js";
import DatePicker from "react-datepicker";
import { format } from "date-fns";
import { getData } from "../../../redux/slices/JobChargeSlice";

const INIT_SUPPLIER_PARAM = { supplier_Id: 0, supplier_Name: "" };

const PARAMS = {
  skip: 0,
  take: 100,
  type: "job_charge",
  sorting: "asc",
  sorting_key: "created_at",
  filter: {},
};

const INIT_FORM_DATA = {
  invoice_Date: "",
  invoice_Due_Date: "",
  supplier_Invoice_Id: 0,
  type: "",
  client_Id: 1,
  staff_Id: 1,
  invoice_Number: "",
  remarks: "",
  supplier_Id: "",
  supplier_Name: "",
  booking_Date: "",
  booking_Number: "",
  services: [],
};

const INIT_APPLICABLE_SERVICES_FORM_DATA = {
  service_Type: "",
  quantity: 0,
  unit_Charge: 0,
  amount: 0,
  supplier_Invoice_data_Id: 0,
  type: "",
};

const CreateSupplierInvoice = ({ invisible, loadData, mode, editData }) => {
  const dispatch = useDispatch();
  const [supplierParam, setSupplierParam] = useState(INIT_SUPPLIER_PARAM);
  const [formData, setFormData] = useState(INIT_FORM_DATA);
  const [validated, setValidated] = useState(false);
  const [masterJobChargeData, setMasterJobChargeData] = useState([]);
  const [applicableServicesFormData, setApplicableServicesFormData] = useState(
    INIT_APPLICABLE_SERVICES_FORM_DATA
  );
  const [applicableServices, setApplicableServices] = useState([]);
  const [autocompleteData, setAutocompleteData] = useState([]);
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const [isSaveDisabled, setIsSaveDisabled] = useState(false);

  useEffect(() => {
    fetchMasterData();
  }, []);

  useEffect(() => {
    if (mode && editData) {
      setFormData((prev) => ({
        ...prev,
        invoice_Date: editData.invoice_Date
          ? format(new Date(editData.invoice_Date), "yyyy-MM-dd")
          : "",
        invoice_Due_Date: checkEmpty(editData?.invoice_Due_Date)
          ? format(new Date(editData.invoice_Due_Date), "yyyy-MM-dd")
          : "",
        supplier_Invoice_Id: editData.supplier_Invoice_Id || 0,
        type: editData.type || "",
        client_Id: editData.client_Id || 1,
        staff_Id: editData.staff_Id || 1,
        invoice_Number: editData.invoice_Number || "",
        remarks: editData.remarks || "",
        supplier_Id: editData.supplier_Id || "",
        supplier_Name: editData.supplier_Name || "",
        booking_Date: editData.booking_Date
          ? format(new Date(editData.booking_Date), "yyyy-MM-dd")
          : "",
        booking_Number: editData.booking_Number || "",
        services: editData.inv_data || [],
      }));

      setSupplierParam({
        supplier_Id: editData.supplier_Id || 0,
        supplier_Name: editData.supplier_Name || "",
      });

      setApplicableServices(editData.inv_data || []);
    }
  }, [mode, editData]);

  const fetchMasterData = async () => {
    try {
      const { payload } = await dispatch(getData({ inputData: PARAMS }));
      const filteredData =
        payload?.job_charge?.filter(
          (item) => item.charge_Type === "Cost of Revenue"
        ) || [];
      setMasterJobChargeData(filteredData);
    } catch {
      toastMessage("error", "Failed to fetch data");
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target || {};
    setFormData((prev) => {
      const updatedValue =
        value && ["invoice_Date", "invoice_Due_Date", "booking_Date"].includes(name)
          ? format(value, "yyyy-MM-dd") +
            (name === "invoice_Due_Date" ? " 23:59:59" : " 00:00:00")
          : value || "";
      return { ...prev, [name]: updatedValue };
    });
  };

  const getCustomerList = async (e) => {
    const { name, value } = e.target;

    setSupplierParam((prev) => ({ ...prev, [name]: value }));

    if (!value.trim()) {
      setAutocompleteData([]);
      setShowAutocomplete(false);
      return;
    }

    const param = {
      searchStr: value,
      skip: 0,
      take: 1000,
      sorting_key: "created_at",
      sorting: "asc",
    };

    const { payload } = await dispatch(fetchSupplierData({ inputData: param }));
    if (payload?.data?.length > 0) {
      setAutocompleteData(payload.data);
      setShowAutocomplete(true);
    } else {
      setAutocompleteData([]);
      setShowAutocomplete(false);
    }
  };

  const selectSupplierData = (supplier) => {
    setSupplierParam({
      supplier_Id: supplier.supplier_Id,
      supplier_Name: supplier.supplier_Name,
    });
    setAutocompleteData([]);
    setShowAutocomplete(false);
  };

  const addApplicableServices = () => {
    const f = applicableServicesFormData;
    if (!f.service_Type || f.quantity <= 0 || f.unit_Charge <= 0) {
      toastMessage("error", "Please enter valid service details");
      return;
    }
    setApplicableServices((prev) => [...prev, f]);
    setApplicableServicesFormData(INIT_APPLICABLE_SERVICES_FORM_DATA);
  };

  const handleApplicableRateInputChange = (e) => {
    const { name, value } = e.target;
    setApplicableServicesFormData((prevData) => {
      let updatedValue = value;
      if (["quantity", "unit_Charge"].includes(name)) {
        updatedValue = value ? parseFloat(value) || 0 : 0;
      }
      const updated = { ...prevData, [name]: updatedValue };
      updated.amount = (updated.quantity || 0) * (updated.unit_Charge || 0);
      return updated;
    });
  };

  const removeApplicableRate = (index, idToRemove) => {
    if (mode) {
      setApplicableServices((prevArray) => {
        const updated = prevArray.map((item) =>
          item.supplier_Invoice_Id === idToRemove ? { ...item, type: "delete" } : item
        );
        setFormData((prevFormData) => ({ ...prevFormData, services: updated }));
        return updated;
      });
    } else {
      setApplicableServices((prev) => prev.filter((_, i) => i !== index));
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const form = event.currentTarget;
    if (form.checkValidity() === false) {
      event.stopPropagation();
    } else {
      handleSave();
    }
    setValidated(true);
  };

  const handleSave = async () => {
    if (applicableServices.length === 0) {
      toastMessage("error", "Please enter atleast 1 applicable services");
      return;
    }
    setIsSaveDisabled(true);

    const payload = {
      ...formData,
      supplier_Id: supplierParam.supplier_Id,
      supplier_Name: supplierParam.supplier_Name,
      services: applicableServices,
    };

    try {
      const response = await dispatch(createSupplierInvoice({ inputData: payload }));
      if (response?.payload?.status === "success") {
        setIsSaveDisabled(false);
        toastMessage("success", "Invoice created successfully!");
        setFormData(INIT_FORM_DATA);
        setSupplierParam(INIT_SUPPLIER_PARAM);
        setApplicableServices([]);
        setValidated(false);
        invisible();
        loadData();
      } else {
        setIsSaveDisabled(false);
        throw new Error(response?.payload?.message || "Failed to create invoice");
      }
    } catch (error) {
      setIsSaveDisabled(false);
      toastMessage("error", error.message);
    }
  };

  return (
    <>
      <CForm
        className="supplier-invoice-form g-3 needs-validation"
        noValidate
        validated={validated}
        onSubmit={handleSubmit}
      >
        {/* ===== INVOICE DETAILS ===== */}
        <Card className="mb-4">
          <Card.Header className="pb-0">
            <h3 className="card-title mb-0">Invoice Details</h3>
          </Card.Header>
          <Card.Body className="pt-3">
            {/* Row 1: Invoice Date | Supplier Name */}
            <Row className="g-3 mb-2">
              <CCol md={6}>
                <CFormLabel htmlFor="invoice_Date" className="main-content-label tx-11 tx-medium tx-gray-600">
                  INVOICE DATE
                </CFormLabel>
                <InputGroup className="reactdate-pic">
                  <InputGroup.Text>
                    <i className="typcn typcn-calendar-outline tx-24 lh--9 op-6"></i>
                  </InputGroup.Text>
                  <DatePicker
                    className="form-control"
                    name="invoice_Date"
                    id="invoice_Date"
                    selected={formData.invoice_Date ? new Date(formData.invoice_Date) : null}
                    onChange={(date) => handleInputChange({ target: { name: "invoice_Date", value: date } })}
                    dateFormat="yyyy-MM-dd"
                    required
                  />
                </InputGroup>
              </CCol>

              <CCol md={6}>
                <CFormLabel htmlFor="supplier_Name" className="main-content-label tx-11 tx-medium tx-gray-600">
                  SUPPLIER NAME
                </CFormLabel>
                <div className="hsc-wrapper position-relative">
                  <CFormInput
                    type="text"
                    id="supplier_Name"
                    name="supplier_Name"
                    value={supplierParam.supplier_Name}
                    onChange={getCustomerList}
                    required
                    autoComplete="off"
                  />
                  {autocompleteData?.length > 0 && showAutocomplete && (
                    <div
                      className="header-search-container"
                      onMouseDown={(e) => e.preventDefault()}
                      onPointerDown={(e) => e.preventDefault()}
                    >
                      <div className="hs">
                        {autocompleteData?.map((suggestion, index) => (
                          <div
                            key={suggestion?.supplier_Id || index}
                            className="main-message-list header-search-element"
                            onPointerDown={(e) => {
                              e.preventDefault();
                              selectSupplierData(suggestion);
                            }}
                            style={{ cursor: "pointer" }}
                          >
                            <span className="user-name">{suggestion?.supplier_Name}</span>
                            <span>{suggestion?.supplier_Email}</span>
                            <span>{suggestion?.supplier_Mobile}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </CCol>
            </Row>

            {/* Row 2: Invoice Number | Booking Date */}
            <Row className="g-3 mb-2">
              <CCol md={6}>
                <CFormLabel htmlFor="invoice_Number" className="main-content-label tx-11 tx-medium tx-gray-600">
                  INVOICE NUMBER
                </CFormLabel>
                <CFormInput
                  type="text"
                  id="invoice_Number"
                  name="invoice_Number"
                  value={formData.invoice_Number}
                  onChange={handleInputChange}
                  required
                />
              </CCol>

              <CCol md={6}>
                <CFormLabel htmlFor="booking_Date" className="main-content-label tx-11 tx-medium tx-gray-600">
                  BOOKING DATE
                </CFormLabel>
                <InputGroup className="reactdate-pic">
                  <InputGroup.Text>
                    <i className="typcn typcn-calendar-outline tx-24 lh--9 op-6"></i>
                  </InputGroup.Text>
                  <DatePicker
                    className="form-control"
                    name="booking_Date"
                    id="booking_Date"
                    selected={formData.booking_Date ? new Date(formData.booking_Date) : null}
                    onChange={(date) => handleInputChange({ target: { name: "booking_Date", value: date } })}
                    dateFormat="yyyy-MM-dd"
                  />
                </InputGroup>
              </CCol>
            </Row>

            {/* Row 3: Remarks full width */}
            <Row className="g-3">
              <CCol md={12}>
                <CFormLabel htmlFor="remarks" className="main-content-label tx-11 tx-medium tx-gray-600">
                  REMARKS
                </CFormLabel>
                <CFormInput
                  type="text"
                  id="remarks"
                  name="remarks"
                  value={formData.remarks}
                  onChange={handleInputChange}
                />
              </CCol>
            </Row>
          </Card.Body>
        </Card>

        {/* ===== APPLICABLE SERVICES ===== */}
        <Card className="mb-4">
          <Card.Header className="pb-0">
            <h3 className="card-title mb-0">Applicable Services</h3>
          </Card.Header>
          <Card.Body className="pt-3">
            <Row className="g-3 align-items-end mb-2">
              <CCol md={5}>
                <CFormLabel htmlFor="service_Type" className="main-content-label tx-11 tx-medium tx-gray-600">
                  DESCRIPTION OF THE CHARGES
                </CFormLabel>
                <CFormInput
                  type="text"
                  id="service_Type"
                  name="service_Type"
                  value={applicableServicesFormData.service_Type}
                  onChange={handleApplicableRateInputChange}
                />
              </CCol>

              <CCol md={2}>
                <CFormLabel htmlFor="unit_Charge" className="main-content-label tx-11 tx-medium tx-gray-600">
                  RATE
                </CFormLabel>
                <CFormInput
                  type="number"
                  step="0.01"
                  id="unit_Charge"
                  name="unit_Charge"
                  value={applicableServicesFormData.unit_Charge}
                  onChange={handleApplicableRateInputChange}
                />
              </CCol>

              <CCol md={2}>
                <CFormLabel htmlFor="quantity" className="main-content-label tx-11 tx-medium tx-gray-600">
                  QUANTITY
                </CFormLabel>
                <CFormInput
                  type="number"
                  step="1"
                  id="quantity"
                  name="quantity"
                  value={applicableServicesFormData.quantity}
                  onChange={handleApplicableRateInputChange}
                />
              </CCol>

              <CCol md={2}>
                <CFormLabel htmlFor="amount" className="main-content-label tx-11 tx-medium tx-gray-600">
                  AMOUNT
                </CFormLabel>
                <CFormInput
                  type="number"
                  step="0.01"
                  id="amount"
                  name="amount"
                  value={applicableServicesFormData.amount}
                  onChange={handleApplicableRateInputChange}
                />
              </CCol>

              <CCol md={1} className="d-flex">
                <CButton color="primary" className="btn btn-sm w-100" onClick={addApplicableServices}>
                  +
                </CButton>
              </CCol>
            </Row>

            {applicableServices
              ?.filter((item) => item.type !== "delete")
              .map((item, index) => (
                <Row className="g-3 mb-2" key={index}>
                  <CCol md={5}>
                    <CFormInput type="text" value={item.service_Type} disabled />
                  </CCol>
                  <CCol md={2}>
                    <CFormInput type="text" value={item.unit_Charge} disabled />
                  </CCol>
                  <CCol md={2}>
                    <CFormInput type="text" value={item.quantity} disabled />
                  </CCol>
                  <CCol md={2}>
                    <CFormInput type="text" value={item.amount} disabled />
                  </CCol>
                  <CCol md={1} className="d-flex">
                    <CButton
                      color="danger"
                      className="btn btn-sm w-100"
                      onClick={() => removeApplicableRate(index, item.supplier_Invoice_Id)}
                    >
                      -
                    </CButton>
                  </CCol>
                </Row>
              ))}
          </Card.Body>
        </Card>

        {/* ===== Actions ===== */}
        <Row className="gy-2">
          <CCol xs={12}>
            <div className="d-flex justify-content-end gap-2">
              <CButton
                color="primary"
                className={`btn btn-sm ${isSaveDisabled ? "btn-loaders" : ""}`}
                type="submit"
                disabled={isSaveDisabled}
              >
                {isSaveDisabled ? <span className="loading">Save Changes</span> : "Save Changes"}
              </CButton>
            </div>
          </CCol>
        </Row>
      </CForm>
    </>
  );
};

export default CreateSupplierInvoice;
